import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Icons from 'lucide-react';
import SiteNav from './marketing/SiteNav';
import SiteFooter from './marketing/SiteFooter';
import { Button } from './ui/button';
import { FRANCHISE_BENEFITS } from '../mock';

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };

const CONTENT = {
  'property-managers': {
    eyebrow: 'For landlords & letting agents',
    title: 'Manage every property service from one dashboard',
    intro: 'Stop juggling inboxes, spreadsheets and multiple suppliers. Book inventories, safety certificates and inspections, then track each job from pending to completed — with reports and compliance documents stored in one place.',
    points: [
      { icon: 'CalendarCheck', title: 'Book in minutes', desc: 'Fixed prices or quotes across four service pillars.' },
      { icon: 'Activity', title: 'Live job tracking', desc: 'See pending, in-progress and completed jobs at a glance.' },
      { icon: 'FileCheck2', title: 'Compliance storage', desc: 'Download PDF reports, invoices and certificates anytime.' },
      { icon: 'Bell', title: 'Stay informed', desc: 'Always know who is assigned and what stage each job is at.' },
    ],
    cta: 'Book a service',
    ctaTo: '/book',
  },
  'contractors': {
    eyebrow: 'For inspectors & engineers',
    title: 'Receive jobs, inspect on-site, upload reports',
    intro: 'Check-a-Property gives contractors a clear queue of assigned jobs. Mark inspections complete, add your findings and upload report summaries — no admin overhead, no chasing.',
    points: [
      { icon: 'Inbox', title: 'Assigned job queue', desc: 'See every job assigned to you with property and schedule details.' },
      { icon: 'ClipboardCheck', title: 'Complete on-site', desc: 'Mark inspections complete and capture your findings.' },
      { icon: 'Upload', title: 'Upload summaries', desc: 'Add text summaries and report data in seconds.' },
      { icon: 'Star', title: 'Build your rating', desc: 'Quality work keeps you top of the assignment list.' },
    ],
    cta: 'Open contractor dashboard',
    ctaTo: '/dashboard',
  },
};

const Solutions = ({ slug: slugProp }) => {
  const navigate = useNavigate();
  const params = useParams();
  const slug = slugProp || params.slug;

  if (slug === 'franchise') {
    return (
      <div className="min-h-screen bg-background">
        <SiteNav />
        <section className="hero-gradient pt-[68px]">
          <div className="max-w-7xl mx-auto px-6 py-24 text-white max-w-3xl">
            <span className="inline-flex items-center gap-2 text-[#7fb0f5] text-sm font-semibold uppercase tracking-wide mb-4"><Icons.Network className="h-4 w-4" /> Enterprise & Franchise</span>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl leading-tight">Scale property services across branches and regions</h1>
            <p className="mt-6 text-lg text-slate-300">For franchise operators and large-scale property managers who need to book and track services without jumping between suppliers, inboxes and spreadsheets.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => navigate('/contact')} className="bg-white text-[#102d55] hover:bg-slate-100 font-semibold px-7">Talk to our team</Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/book')} className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white px-7">Book a service</Button>
            </div>
          </div>
        </section>
        <section className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid sm:grid-cols-2 gap-6">
            {FRANCHISE_BENEFITS.map((b) => (
              <div key={b.title} className="bg-white rounded-2xl border border-border p-8 card-hover flex gap-5">
                <div className="h-12 w-12 rounded-xl bg-[#eaf1fb] text-[#1c63c4] flex items-center justify-center shrink-0"><Icon name={b.icon} className="h-6 w-6" /></div>
                <div><h3 className="font-display font-bold text-lg text-[#102d55]">{b.title}</h3><p className="mt-2 text-muted-foreground">{b.desc}</p></div>
              </div>
            ))}
          </div>
          <div className="mt-12 hero-gradient rounded-2xl p-10 text-center text-white">
            <h2 className="font-display font-extrabold text-3xl">One platform. Every property service.</h2>
            <p className="mt-3 text-slate-300 max-w-xl mx-auto">Centralise ordering, assignment, tracking and compliance for your whole portfolio.</p>
            <Button size="lg" onClick={() => navigate('/contact')} className="mt-7 bg-white text-[#102d55] hover:bg-slate-100 font-semibold px-8">Request enterprise demo</Button>
          </div>
        </section>
        <SiteFooter />
      </div>
    );
  }

  const c = CONTENT[slug] || CONTENT['property-managers'];
  return (
    <div className="min-h-screen bg-background">
      <SiteNav />
      <section className="hero-gradient pt-[68px]">
        <div className="max-w-7xl mx-auto px-6 py-24 text-white">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-[#7fb0f5] text-sm font-semibold uppercase tracking-wide mb-4">{c.eyebrow}</span>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl leading-tight">{c.title}</h1>
            <p className="mt-6 text-lg text-slate-300">{c.intro}</p>
            <Button size="lg" onClick={() => navigate(c.ctaTo)} className="mt-8 bg-white text-[#102d55] hover:bg-slate-100 font-semibold px-7">{c.cta} <Icons.ArrowRight className="ml-2 h-4 w-4" /></Button>
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {c.points.map((p) => (
            <div key={p.title} className="bg-white rounded-2xl border border-border p-6 card-hover">
              <div className="h-11 w-11 rounded-xl bg-[#102d55] text-white flex items-center justify-center mb-4"><Icon name={p.icon} className="h-5 w-5" /></div>
              <h3 className="font-display font-semibold text-[#102d55]">{p.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default Solutions;
